import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Chip,
  SelectChangeEvent,
  Button,
  ButtonGroup,
  Container,
  Avatar,
  Fade,
  Zoom,
  Stack,
  alpha,
  useTheme,
  useMediaQuery,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Shield,
  Security,
  CheckCircle,
  Warning,
  // Error as ErrorIcon,
  Download,
  PictureAsPdf,
  TableChart,
  Gavel,
  // TrendingUp,
  Assessment,
  VerifiedUser,
  // Speed,
  Block
  // Refresh,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ComplianceMetrics from '../components/ComplianceMetrics';

interface ComplianceDashboardData {
  organization_id: number;
  date_range: {
    start: string;
    end: string;
    days: number;
  };
  frameworks: Array<{
    name: string;
    compliance_score: number;
    score_history: Array<{
      date: string;
      score: number;
      phi_detections: number;
      phi_blocked: number;
    }>;
    metrics: {
      total_requests: number;
      phi_detections: number;
      phi_blocked: number;
      phi_redacted: number;
      breach_attempts_blocked: number;
      unauthorized_access_attempts: number;
      audit_events_logged: number;
      average_response_time_ms: number;
      detection_rate: string;
      block_rate: string;
    };
  }>;
  summary: {
    total_requests: number;
    total_phi_detections: number;
    total_phi_blocked: number;
    total_breach_attempts_blocked: number;
    overall_detection_rate: string;
    overall_block_rate: string;
    average_compliance_score: number;
  };
}

const ComplianceDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedFramework, setSelectedFramework] = useState<string>('all');
  const [days, setDays] = useState<number>(30);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'csv'>('pdf');
  const { isAuthenticated } = useAuth();

  const {
    data: dashboardData,
    isLoading,
    error
  } = useQuery<ComplianceDashboardData>({
    queryKey: ['complianceDashboard', selectedFramework, days],
    queryFn: async () => {
      const params: any = { days };
      if (selectedFramework !== 'all') {
        params.framework = selectedFramework;
      }
      const response = await axios.get('/api/compliance/dashboard', { params });
      return response.data;
    },
    enabled: isAuthenticated,
    refetchInterval: 60000 // Refetch every minute
  });

  const handleFrameworkChange = (event: SelectChangeEvent<string>) => {
    setSelectedFramework(event.target.value);
  };

  const handleDaysChange = (event: SelectChangeEvent<number>) => {
    setDays(event.target.value as number);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const response = await axios.post(
        '/api/compliance/export',
        {
          format: exportFormat,
          framework: selectedFramework === 'all' ? null : selectedFramework,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          include_rules: true,
          include_violations: true
        },
        {
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `compliance-report-${selectedFramework}-${Date.now()}.${exportFormat}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  const getScoreStatus = (score: number) => {
    if (score >= 95) return 'Excellent';
    if (score >= 85) return 'Good';
    if (score >= 70) return 'Fair';
    if (score >= 50) return 'Needs Improvement';
    return 'Critical';
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          gap={2}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="body1" color="text.secondary">
            Loading compliance data...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
          }}
        >
          Failed to load compliance dashboard data. Please try again.
        </Alert>
      </Container>
    );
  }

  const currentFramework =
    selectedFramework === 'all'
      ? null
      : dashboardData?.frameworks.find((f) => f.name === selectedFramework);

  const displayScore = currentFramework
    ? currentFramework.compliance_score
    : dashboardData?.summary.average_compliance_score || 0;

  const displayMetrics = currentFramework ? currentFramework.metrics : dashboardData?.summary;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Enhanced Header with Gradient Background */}
      <Fade in timeout={600}>
        <Box
          sx={{
            mb: 4,
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '300px',
              height: '300px',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
              borderRadius: '50%',
              transform: 'translate(30%, -30%)'
            }
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={3}
          >
            <Box display="flex" alignItems="center" gap={2.5}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`
                }}
              >
                <Gavel sx={{ fontSize: 32, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  fontWeight={700}
                  sx={{
                    mb: 0.5,
                    background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Compliance Dashboard
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <VerifiedUser sx={{ fontSize: 16 }} />
                  Monitor regulatory compliance and security metrics
                </Typography>
              </Box>
            </Box>

            <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} flexWrap="wrap">
              <FormControl
                size="small"
                sx={{
                  minWidth: 150,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    '&:hover': {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                    }
                  }
                }}
              >
                <InputLabel>Time Period</InputLabel>
                <Select value={days} onChange={handleDaysChange} label="Time Period">
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    '&:hover': {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
                    }
                  }
                }}
              >
                <InputLabel>Framework</InputLabel>
                <Select
                  value={selectedFramework}
                  onChange={handleFrameworkChange}
                  label="Framework"
                >
                  <MenuItem value="all">All Frameworks</MenuItem>
                  {dashboardData?.frameworks.map((fw) => (
                    <MenuItem key={fw.name} value={fw.name}>
                      {fw.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <ButtonGroup
                size="small"
                variant="outlined"
                sx={{
                  '& .MuiButton-root': {
                    borderWidth: 1.5,
                    '&:hover': {
                      borderWidth: 1.5
                    }
                  }
                }}
              >
                <Button
                  variant={exportFormat === 'pdf' ? 'contained' : 'outlined'}
                  onClick={() => setExportFormat('pdf')}
                  startIcon={<PictureAsPdf />}
                >
                  PDF
                </Button>
                <Button
                  variant={exportFormat === 'csv' ? 'contained' : 'outlined'}
                  onClick={() => setExportFormat('csv')}
                  startIcon={<TableChart />}
                >
                  CSV
                </Button>
              </ButtonGroup>

              <Tooltip title="Export compliance report">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    exportLoading ? <CircularProgress size={20} color="inherit" /> : <Download />
                  }
                  onClick={handleExport}
                  disabled={exportLoading || dashboardData?.frameworks.length === 0}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Export
                </Button>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      </Fade>

      {dashboardData?.frameworks.length === 0 && (
        <Fade in timeout={800}>
          <Alert
            severity="info"
            sx={{
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
            }}
          >
            No compliance frameworks are currently enabled. Please enable at least one framework in
            Organization Settings.
          </Alert>
        </Fade>
      )}

      <Grid container spacing={3}>
        {/* Enhanced Compliance Score Card */}
        <Grid item xs={12} md={4}>
          <Zoom in timeout={600}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 2.5,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                    }}
                  >
                    <Shield sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    Compliance Score
                  </Typography>
                </Box>

                <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                  <Box position="relative" display="inline-flex">
                    <CircularProgress
                      variant="determinate"
                      value={displayScore}
                      size={140}
                      thickness={5}
                      color={getScoreColor(displayScore)}
                      sx={{
                        '& .MuiCircularProgress-circle': {
                          strokeLinecap: 'round'
                        }
                      }}
                    />
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      bottom={0}
                      right={0}
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography
                        variant="h3"
                        component="div"
                        fontWeight={700}
                        color="text.primary"
                      >
                        {displayScore}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        out of 100
                      </Typography>
                    </Box>
                  </Box>

                  <Chip
                    label={getScoreStatus(displayScore)}
                    color={getScoreColor(displayScore)}
                    sx={{
                      mt: 3,
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      px: 2,
                      height: 32
                    }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ fontWeight: 500 }}
                >
                  {selectedFramework === 'all'
                    ? 'Average across all frameworks'
                    : `${selectedFramework} compliance score`}
                </Typography>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        {/* Enhanced PHI Protection Card */}
        <Grid item xs={12} md={4}>
          <Zoom in timeout={600} style={{ transitionDelay: '100ms' }}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 2.5,
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.info.main, 0.2)}`,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`
                    }}
                  >
                    <Security sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    PHI Protection
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      Detection Rate
                    </Typography>
                    <Chip
                      label={
                        'overall_detection_rate' in (displayMetrics || {})
                          ? (displayMetrics as any).overall_detection_rate
                          : 'detection_rate' in (displayMetrics || {})
                            ? (displayMetrics as any).detection_rate
                            : '0%'
                      }
                      size="small"
                      color="info"
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={parseFloat(
                      'overall_detection_rate' in (displayMetrics || {})
                        ? (displayMetrics as any).overall_detection_rate
                        : 'detection_rate' in (displayMetrics || {})
                          ? (displayMetrics as any).detection_rate
                          : '0'
                    )}
                    color="info"
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.info.main, 0.1)
                    }}
                  />
                </Box>

                <Box mb={3}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      Block Rate
                    </Typography>
                    <Chip
                      label={
                        'overall_block_rate' in (displayMetrics || {})
                          ? (displayMetrics as any).overall_block_rate
                          : 'block_rate' in (displayMetrics || {})
                            ? (displayMetrics as any).block_rate
                            : '0%'
                      }
                      size="small"
                      color="success"
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={parseFloat(
                      'overall_block_rate' in (displayMetrics || {})
                        ? (displayMetrics as any).overall_block_rate
                        : 'block_rate' in (displayMetrics || {})
                          ? (displayMetrics as any).block_rate
                          : '0'
                    )}
                    color="success"
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.success.main, 0.1)
                    }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Detected
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="info.main" mt={0.5}>
                        {'total_phi_detections' in (displayMetrics || {})
                          ? (displayMetrics as any).total_phi_detections
                          : 'phi_detections' in (displayMetrics || {})
                            ? (displayMetrics as any).phi_detections
                            : 0}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Blocked
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="success.main" mt={0.5}>
                        {'total_phi_blocked' in (displayMetrics || {})
                          ? (displayMetrics as any).total_phi_blocked
                          : 'phi_blocked' in (displayMetrics || {})
                            ? (displayMetrics as any).phi_blocked
                            : 0}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        {/* Enhanced Security Events Card */}
        <Grid item xs={12} md={4}>
          <Zoom in timeout={600} style={{ transitionDelay: '200ms' }}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 2.5,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.05)} 0%, ${alpha(theme.palette.warning.main, 0.02)} 100%)`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.2)}`,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`
                    }}
                  >
                    <Warning sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    Security Events
                  </Typography>
                </Box>

                <Stack spacing={2.5}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.success.main, 0.12),
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(theme.palette.success.main, 0.2)
                        }}
                      >
                        <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                      </Box>
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Breach Attempts Blocked
                        </Typography>
                        <Typography variant="h5" fontWeight={700} color="success.main">
                          {'total_breach_attempts_blocked' in (displayMetrics || {})
                            ? (displayMetrics as any).total_breach_attempts_blocked
                            : 'breach_attempts_blocked' in (displayMetrics || {})
                              ? (displayMetrics as any).breach_attempts_blocked
                              : 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.error.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.error.main, 0.12),
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(theme.palette.error.main, 0.2)
                        }}
                      >
                        <Block sx={{ color: 'error.main', fontSize: 20 }} />
                      </Box>
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Unauthorized Access Attempts
                        </Typography>
                        <Typography variant="h5" fontWeight={700} color="error.main">
                          {'unauthorized_access_attempts' in (displayMetrics || {})
                            ? (displayMetrics as any).unauthorized_access_attempts
                            : 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.info.main, 0.12),
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(theme.palette.info.main, 0.2)
                        }}
                      >
                        <Assessment sx={{ color: 'info.main', fontSize: 20 }} />
                      </Box>
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Audit Events Logged
                        </Typography>
                        <Typography variant="h5" fontWeight={700} color="info.main">
                          {'audit_events_logged' in (displayMetrics || {})
                            ? (displayMetrics as any).audit_events_logged
                            : 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        {/* Enhanced Compliance Metrics Visualization */}
        <Grid item xs={12}>
          <Fade in timeout={1000}>
            <Box>
              <ComplianceMetrics
                framework={selectedFramework === 'all' ? null : selectedFramework}
                days={days}
                dashboardData={dashboardData}
              />
            </Box>
          </Fade>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ComplianceDashboard;
